import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';
import type { CordeCommonModelsDtoTeamTeam } from '@/api/generated/v1/api';

interface EditTeamFormData {
  name: string;
  description: string;
  team_type: string;
}

interface TeamEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: CordeCommonModelsDtoTeamTeam;
  onSuccess: () => void;
}

export const TeamEditModal: React.FC<TeamEditModalProps> = ({
  isOpen,
  onClose,
  team,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const teamService = useTeamService();
  
  const [editTeamData, setEditTeamData] = useState<EditTeamFormData>({
    name: '',
    description: '',
    team_type: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (team) {
      setEditTeamData({
        name: team.name || '',
        description: team.description || '',
        team_type: team.team_type || ''
      });
    }
  }, [team]);

  const handleEditTeam = async () => {
    setIsSubmitting(true);
    try {
      await teamService.updateTeam(team.id!, editTeamData);
      toast({ title: t('teams.teamUpdated'), status: 'success' });
      onClose();
      onSuccess();
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('teams.editTeam')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t('teams.teamName')}</FormLabel>
              <Input
                value={editTeamData.name}
                onChange={(e) => setEditTeamData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('teams.enterTeamName')}
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>{t('teams.teamType')}</FormLabel>
              <Select
                value={editTeamData.team_type}
                onChange={(e) => setEditTeamData(prev => ({ ...prev, team_type: e.target.value }))}
              >
                <option value="project">{t('teams.teamTypeProject')}</option>
                <option value="business">{t('teams.teamTypeBusiness')}</option>
                <option value="social">{t('teams.teamTypeSocial')}</option>
                <option value="gaming">{t('teams.teamTypeGaming')}</option>
                <option value="educational">{t('teams.teamTypeEducational')}</option>
                <option value="nonprofit">{t('teams.teamTypeNonprofit')}</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>{t('common.description')}</FormLabel>
              <Textarea
                value={editTeamData.description}
                onChange={(e) => setEditTeamData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('teams.enterTeamDescription')}
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleEditTeam}
            isLoading={isSubmitting}
            colorScheme="blue"
          >
            {t('common.update')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 